// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useEffect, useState } from 'react'
import { Check, Loader2, Plus, X } from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/ui'

import { FormatSpecEditor, type FormatType } from './FormatSpecEditor'

export interface ReportSectionFormData {
  title: string
  content: string
  formatType: FormatType
  formatSpec: string[]
  formatGuidance: string
}

export interface ReportSectionFormProps {
  disabled?: boolean
  saving?: boolean
  initialData?: ReportSectionFormData | null
  onSubmit: (data: ReportSectionFormData) => Promise<void>
  onCancel?: () => void
  onError?: (message: string) => void
}

const DEFAULT_FORM_DATA: ReportSectionFormData = {
  title: '',
  content: '',
  formatType: 'table',
  formatSpec: [],
  formatGuidance: '',
}

/**
 * Simplified form for Report KB sections.
 * Collects: section title, section prompt, output format, and format specs.
 */
export function ReportSectionForm({
  disabled = false,
  saving = false,
  initialData,
  onSubmit,
  onCancel,
  onError,
}: ReportSectionFormProps): JSX.Element {
  const [formData, setFormData] = useState<ReportSectionFormData>(
    initialData ?? DEFAULT_FORM_DATA
  )

  const isEditing = initialData !== null && initialData !== undefined

  // Sync form with initialData when it changes (e.g., when editing different item)
  useEffect(() => {
    setFormData(initialData ?? DEFAULT_FORM_DATA)
  }, [initialData])
  const canSubmit = formData.title.trim().length > 0 && formData.content.trim().length > 0

  const handleFieldChange = useCallback(<K extends keyof ReportSectionFormData>(
    field: K,
    value: ReportSectionFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleFormatTypeChange = useCallback((type: FormatType) => {
    setFormData(prev => ({
      ...prev,
      formatType: type,
      formatSpec: type === 'prose' ? [] : prev.formatSpec,
      formatGuidance: type === 'table' || type === 'bullets' ? '' : prev.formatGuidance,
    }))
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) {
      onError?.('Please fill in the section title and prompt.')
      return
    }

    try {
      await onSubmit({
        title: formData.title.trim(),
        content: formData.content.trim(),
        formatType: formData.formatType,
        formatSpec: formData.formatSpec.filter(s => s.trim().length > 0),
        formatGuidance: formData.formatGuidance.trim(),
      })

      if (!isEditing) {
        setFormData(DEFAULT_FORM_DATA)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save section'
      onError?.(message)
    }
  }, [canSubmit, formData, isEditing, onSubmit, onError])

  const handleCancel = useCallback(() => {
    setFormData(initialData ?? DEFAULT_FORM_DATA)
    onCancel?.()
  }, [initialData, onCancel])

  return (
    <Card
      className={isEditing ? 'ring-2 ring-primary/50 bg-primary/5' : ''}
      data-testid={isEditing ? 'report-section-edit-form' : 'report-section-form'}
    >
      <CardHeader className="pb-4">
        <CardTitle className="text-lg flex items-center gap-2">
          {isEditing ? 'Edit Report Section' : 'Add Report Section'}
          {isEditing && (
            <span className="text-xs font-normal text-primary bg-primary/10 px-2 py-0.5 rounded">
              Editing: {formData.title || 'Untitled'}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="section-title">Section Title *</Label>
          <Input
            id="section-title"
            value={formData.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('title', e.target.value)}
            placeholder="e.g., Negotiation Opportunities"
            disabled={disabled || saving}
            data-testid="report-section-title"
          />
          <p className="text-sm text-slate-600">
            Display name for this report section.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="section-prompt">Section Prompt *</Label>
          <Textarea
            id="section-prompt"
            value={formData.content}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFieldChange('content', e.target.value)}
            placeholder="e.g., Analyze the contract's financial terms and identify the top 5 negotiation opportunities. For each, explain the leverage point and estimate potential savings."
            disabled={disabled || saving}
            rows={4}
            className="resize-none"
            data-testid="report-section-prompt"
          />
          <p className="text-sm text-slate-600">
            The analysis instructions for this section. This prompt tells the LLM what to analyze and extract.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="output-format">Output Format</Label>
          <Select
            value={formData.formatType}
            onValueChange={(value: string) => handleFormatTypeChange(value as FormatType)}
            disabled={disabled || saving}
          >
            <SelectTrigger id="output-format" data-testid="report-section-format">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="table">Table</SelectItem>
              <SelectItem value="bullets">Bullet List</SelectItem>
              <SelectItem value="prose">Prose Paragraph</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-slate-600">
            How the LLM should format its response.
          </p>
        </div>

        <FormatSpecEditor
          formatType={formData.formatType}
          formatSpec={formData.formatSpec}
          formatGuidance={formData.formatGuidance}
          disabled={disabled || saving}
          onFormatSpecChange={spec => handleFieldChange('formatSpec', spec)}
          onFormatGuidanceChange={guidance => handleFieldChange('formatGuidance', guidance)}
        />

        <div className="flex justify-end gap-2 pt-2">
          {(isEditing || onCancel) && (
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={saving}
              data-testid="report-section-cancel"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          )}
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit || disabled || saving}
            variant={isEditing ? 'default' : 'default'}
            data-testid={isEditing ? 'report-section-save' : 'report-section-submit'}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : isEditing ? (
              <Check className="h-4 w-4 mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            {isEditing ? 'Save Changes' : 'Add Section'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
